import { ISidebar, ISidebarItem } from "../../types/ISidebar";
import React, { useEffect, useRef, useState } from "react";
import { ChevronDownIcon, XIcon } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useSidebarStore } from "../Layout/Store";
import CompanySwitcher from "../CompanySwitcher";
import S from "./styles.module.scss";
import { SIDEBAR_SECTIONS } from "./sidebarItems";
import HorizontalLogo from "../../../public/logo/horizontal.svg";
import { useLoggedUserStore } from "@/Store";
import { isRouteActive } from "@/Utils";
import { resolvePageMeta } from "@/Router/pageMeta";

const renderIcon = (icon: ISidebarItem["icon"], size: number) =>
  React.createElement(icon, { size });

/**
 * Tooltip de um link. A descrição vem do `pageMeta` da rota, que é a mesma
 * usada como subtítulo da página — assim os dois textos não divergem.
 */
const hintDaRota = (path: string) => resolvePageMeta(path).description;

/** Itens sem página própria (o grupo "Ponto") trazem a descrição no dado. */
const itemHint = (item: ISidebarItem) =>
  item.description ?? (item.path ? hintDaRota(item.path) : undefined);

const Sidebar = ({ sidebarData }: ISidebar) => {
  const loggedUser = useLoggedUserStore((state) => state.loggedUser);
  const location = useLocation();

  const { isSidebarOpen, handleOpenSidebar, closeSidebar } = useSidebarStore();
  const sidebarRef = useRef(null);
  const [toggledGroups, setToggledGroups] = useState<Record<string, boolean>>(
    {}
  );

  const sidebar = sidebarData.map((item) => {
    if (!item.submenu) {
      return { ...item, active: isRouteActive(location.pathname, item.path) };
    }

    const activePath = item.submenu
      .map((subitem) => subitem.path)
      .filter((path) => isRouteActive(location.pathname, path))
      .sort((a, b) => (b?.length ?? 0) - (a?.length ?? 0))[0];

    const submenu = item.submenu.map((subitem) => ({
      ...subitem,
      active: Boolean(activePath) && subitem.path === activePath,
    }));

    return {
      ...item,
      active: submenu.some((subitem) => subitem.active),
      submenu,
    };
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        closeSidebar();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [closeSidebar]);

  const renderItem = (item: ISidebarItem, key: string) => {
    if (!item.submenu) {
      return (
        <li
          key={key}
          className={`${S["sidebar-list-item"]} ${item.active ? S.active : ""}`}
        >
          <Link
            className={S["sidebar-item"]}
            to={(item.path ?? "#").toString()}
            title={itemHint(item)}
            onClick={closeSidebar}
          >
            {renderIcon(item.icon, 20)}
            <span className={S["item-label"]}>
              {item.path === "/contas" && loggedUser?.type === "support"
                ? "Atendimento"
                : String(item.label)}
            </span>
          </Link>
        </li>
      );
    }

    const isOpen = toggledGroups[key] ?? item.active;

    return (
      <li key={key} className={S["sidebar-group"]}>
        <button
          type="button"
          className={`${S["sidebar-item"]} ${item.active ? S.groupActive : ""}`}
          aria-expanded={isOpen}
          title={itemHint(item)}
          onClick={() =>
            setToggledGroups((state) => ({ ...state, [key]: !isOpen }))
          }
        >
          {renderIcon(item.icon, 20)}
          <span className={S["item-label"]}>{String(item.label)}</span>
          <ChevronDownIcon
            size={12}
            className={`${S.chevron} ${isOpen ? S.chevronOpen : ""}`}
            aria-hidden="true"
          />
        </button>

        <div className={`${S["submenu-wrap"]} ${isOpen ? S.submenuOpen : ""}`}>
          <ul className={S.submenu}>
            {item.submenu.map((subitem) => (
              <li key={subitem.path}>
                <Link
                  to={subitem.path}
                  onClick={closeSidebar}
                  title={hintDaRota(subitem.path)}
                  className={`${S["submenu-item"]} ${
                    subitem.active ? S.active : ""
                  }`}
                >
                  {subitem.icon ? (
                    renderIcon(subitem.icon, 16)
                  ) : (
                    <span className={S["submenu-dot"]} aria-hidden="true" />
                  )}
                  <span className={S["item-label"]}>{subitem.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </li>
    );
  };

  const isPlatformStaff =
    loggedUser?.type === "superadmin" || loggedUser?.type === "support";
  const isAdmin = ["superadmin", "owner", "admin"].includes(
    loggedUser?.type ?? ""
  );
  const userModules = loggedUser?.modules ?? [];
  const visible = sidebar.filter((s) => {
    if ("ownerOnly" in s && s.ownerOnly) return loggedUser?.type === "owner";
    if ("accountOwnerOrStaff" in s && s.accountOwnerOrStaff)
      return loggedUser?.type === "owner" || isPlatformStaff;
    if (s.supportVisible && loggedUser?.type === "support") return true;
    if (s.superAdmin) return isPlatformStaff;
    if (isAdmin) return true;
    // member: item de feature exige o módulo liberado; itens gerais (Comum) sempre.
    if (s.module) return userModules.includes(s.module);
    return s.permission?.includes("Comum");
  });
  const geralItems = visible.filter((s) => s.context === "geral");
  const empresaItems = visible.filter((s) => s.context !== "geral");
  // Seção só existe depois do filtro: some quando nenhum item dela é visível.
  const secoes = SIDEBAR_SECTIONS.map((section) => ({
    ...section,
    items: empresaItems.filter((item) => item.section === section.key),
  })).filter((section) => section.items.length > 0);
  const semSecao = empresaItems.filter((item) => !item.section);

  const renderLista = (items: typeof empresaItems, prefixo: string) => (
    <ul className={S["sidebar-list"]}>
      {items.map((item, index) => renderItem(item, `${prefixo}-${index}`))}
    </ul>
  );

  return (
    <aside
      ref={sidebarRef}
      className={`${S.sidebar} ${isSidebarOpen ? S.open : ""}`}
    >
      <div className={S["sidebar-header"]}>
        <HorizontalLogo className={S.logo} role="img" aria-label="Genialogic" />
        <XIcon
          className={S["close-icon"]}
          size={24}
          onClick={handleOpenSidebar}
        />
      </div>
      <nav className={S.nav} aria-label="Navegação principal">
        {geralItems.length > 0 && (
          <section className={S.section}>
            <h2 className={S["section-title"]}>Geral</h2>
            {renderLista(geralItems, "g")}
          </section>
        )}
        {empresaItems.length > 0 && (
          <div className={S["company-section"]}>
            <span className={S["section-eyebrow"]}>Empresa ativa</span>
            <div className={S["switcher-wrap"]}>
              <CompanySwitcher variant="sidebar" />
            </div>
            {secoes.map((section) => (
              <section key={section.key} className={S.section}>
                <h2 className={S["section-title"]}>{section.label}</h2>
                {renderLista(section.items, section.key)}
              </section>
            ))}
            {semSecao.length > 0 && (
              <section className={S.section}>
                {renderLista(semSecao, "s")}
              </section>
            )}
          </div>
        )}
      </nav>
    </aside>
  );
};

export default Sidebar;
